import React, { useEffect, useState } from "react";
import {
  FormControl,
  MenuItem,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Radio,
  RadioGroup,
  FormLabel,
  FormControlLabel,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import DebugPanel from "./DebugPanel";
import { useMsal } from "@azure/msal-react";

const validationSchema = Yup.object({
  purpose: Yup.string().required(
    "Pole wymagane. Wybierz cel zgłoszenia sprawy!"
  ),
  regionalOffice: Yup.string().required("Pole wymagane. Wybierz chorągiew!"),
  description: Yup.string().required("Pole wymagane. Podaj opis sytuacji!"),
  ageVictim: Yup.number()
    .min(0, "Wiek musi być większy lub równy 0!")
    .max(100, "Wiek musi być mniejszy lub równy 100!"),
  ageGuilty: Yup.number()
    .min(0, "Wiek musi być większy lub równy 0!")
    .max(100, "Wiek musi być mniejszy lub równy 100!"),
  age: Yup.number()
    .min(0, "Wiek musi być większy lub równy 0!")
    .max(100, "Wiek musi być mniejszy lub równy 100!"),
});

export default function Questions({
  setError,
  setSuccess,
  formType,
  affectType,
}) {
  const { accounts } = useMsal();
  const user = accounts[0];

  const [regionalOffices, setRegionalOffices] = useState([]);
  const [loadingRegionalOffices, setLoadingRegionalOffices] = useState(true);
  const [districtOffices, setDistrictOffices] = useState([]);
  const [loadingDistrictOffices, setLoadingDistrictOffices] = useState(true);

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_URL + "/api/GetRegionalOffices")
      .then((res) => {
        setRegionalOffices(res.data);
      })
      .catch((err) => {
        setError(
          "[Chorągwie] Błąd pobierania danych chorągwi",
          err && " (" + err + ")"
        );
      })
      .finally(() => {
        setLoadingRegionalOffices(false);
      });
  }, [setError]);

  const formik = useFormik({
    initialValues: {
      anonymous: formType,
      affectsYou: affectType,
      purpose: "",
      nameSurname: formType === 2 && affectType === 1 ? user.name : "",
      phoneNumber: "",
      emailAddress: formType === 2 ? user.username : "",
      role: "",
      age: "",
      nameSurnameVictim: formType === 2 && affectType === 0 ? user.name : "",
      roleVictim: "",
      regionalOffice: "",
      districtOffice: "",
      nameSurnameGuilty: "",
      ageVictim: "",
      ageGuilty: "",
      infoGuilty: "",
      description: "",
      isCrime: "",
      policeInformed: "",
      isCamp: "",
      action: "",
      nameSurnameCampCommisioner: "",
      emailAddressCampCommisioner: "",
      phoneNumberCampCommisioner: "",
      isDistrictCommisionerGuilty: "",
      isRegionalOfficeCommisionerGuilty: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        const response = await axios.post(
          process.env.REACT_APP_API_URL + "/api/AddIssue",
          values
        );
        if (response.status >= 200 && response.status < 300) {
          setSuccess({
            alert: "Dziękujemy za przesłanie tego zgłoszenia!",
            description:
              "Sprawa zostanie przekazana do odpowiedniego komendanta hufca, chorągwi lub na poziom centralny (w zależności od tego, która z opcji została wybrana w formularzu). Jeśli podałeś/aś sój numer telefonu może skontaktować się z Tobą Strefa Pomocy ZHP lub osoba prowadząca interwencję w środowisku działania, aby uzyskać więcej informacji. Dziękujemy za Twoje zaangażowanie w budowanie kultury bezpieczeństwa w ZHP! Twoje zgłoszenie otrzymało numer:",
            pre: response.data.key,
          });
          resetForm();
        } else {
          throw new Error("Nieoczekiwana odpowiedź serwera");
        }
      } catch (error) {
        setError(
          error.response?.data?.message ||
            "Wystąpił błąd podczas wysyłania zgłoszenia."
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <>
      <DebugPanel title="Formik" data={formik} />
      <DebugPanel title="Sate" data={{ formType, affectType }} />

      <Box component="form" onSubmit={formik.handleSubmit}>
        <TextField
          fullWidth
          select
          variant="filled"
          label="W jakim celu zgłaszasz tę sprawę?"
          name="purpose"
          value={formik.values.purpose}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.purpose && Boolean(formik.errors.purpose)}
          helperText={
            formik.touched.purpose && formik.errors.purpose
              ? formik.errors.purpose
              : "Pole wymagane."
          }
          margin="normal"
        >
          <MenuItem disabled>
            <em>Wybierz...</em>
          </MenuItem>
          <MenuItem value="Uzyskanie wsparcia i pomocy dla siebie">
            Uzyskanie wsparcia i pomocy dla siebie
          </MenuItem>
          <MenuItem value="Uzyskanie wsparcia w rozwiązaniu trudnej sytuacji innej osoby">
            Uzyskanie wsparcia w rozwiązaniu trudnej sytuacji innej osoby
          </MenuItem>
          <MenuItem value="Uzyskanie zmiany w środowisku działania">
            Uzyskanie zmiany w środowisku działania
          </MenuItem>
          <MenuItem value="Ukaranie kogoś">Ukaranie kogoś</MenuItem>
          <MenuItem value="Nie wiem – kazano mi to zgłosić">
            Nie wiem – kazano mi to zgłosić
          </MenuItem>
        </TextField>
        {affectType === 1 && formType !== 0 && (
          <>
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Osoba zgłaszająca
              </Typography>
              <Typography>
                Jeśli podasz swoje dane kontaktowe w tym formularzu, zostaną one
                wykorzystane do kontaktu z Tobą w celu weryfikacji tego, jak
                czujesz się po dokonaniu zgłoszenia i czy nie potrzebujesz
                dodatkowej pomocy oraz zostaną przekazane osobie prowadzącej
                interwencję w tej sprawie (jeśli nie jesteś nią Ty). Pytanie o
                funkcję w ZHP pozwoli nam wyłapać te osoby, które będą
                zaangażowane w dalszy proces prowadzenia interwencji w tej
                sprawie (np. komendant hufca, kierownik wypoczynku). W takiej
                sytuacji nasza rozmowa będzie miała też na celu opracowanie z
                Tobą planu działań, które należy podjąć.
              </Typography>
            </Box>

            <TextField
              fullWidth
              variant="filled"
              label="Twoje imię i nazwisko"
              name="nameSurname"
              margin="normal"
              value={formik.values.nameSurname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.nameSurname && Boolean(formik.errors.nameSurname)
              }
              helperText={
                formik.touched.nameSurname && formik.errors.nameSurname
                  ? formik.errors.nameSurname
                  : "Pamiętaj, że podanie nam swoich danych jest dobrowolne!"
              }
              disabled={formType === 2}
            />

            <TextField
              fullWidth
              type="number"
              variant="filled"
              label="Twój numer telefonu"
              name="phoneNumber"
              margin="normal"
              value={formik.values.phoneNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)
              }
              helperText={
                formik.touched.phoneNumber && formik.errors.phoneNumber
                  ? formik.errors.phoneNumber
                  : "Twój telefon zostanie wykorzystany do nawiązania kontaktu z Tobą w celu weryfikacji tego, jak czujesz się po dokonaniu zgłoszenia i czy nie potrzebujesz dodatkowej pomocy oraz zostaną przekazane osobie prowadzącej interwencję w tej sprawie (jeśli nie jesteś nią Ty)."
              }
            />

            <TextField
              fullWidth
              type="email"
              variant="filled"
              label="Twój adres e-mail"
              name="emailAddress"
              margin="normal"
              value={formik.values.emailAddress}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.emailAddress &&
                Boolean(formik.errors.emailAddress)
              }
              helperText={
                formik.touched.emailAddress && formik.errors.emailAddress
                  ? formik.errors.emailAddress
                  : "Twój adres e-mail zostanie wykorzystany do kontaktu z Tobą."
              }
              disabled={formType === 2}
            />

            <TextField
              fullWidth
              variant="filled"
              label="Twoja funkcja"
              name="role"
              margin="normal"
              value={formik.values.role}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.role && Boolean(formik.errors.role)}
              helperText={
                formik.touched.role && formik.errors.role
                  ? formik.errors.role
                  : "Jaką funkcję pełnisz w ZHP? Jeśli nie pełnisz żadnej, przejdź dalej."
              }
            />

            <TextField
              fullWidth
              type="number"
              variant="filled"
              label="Twój wiek"
              name="age"
              margin="normal"
              value={formik.values.age}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.age && Boolean(formik.errors.age)}
              helperText={
                formik.touched.age && formik.errors.age
                  ? formik.errors.age
                  : "Ile masz lat?"
              }
            />
          </>
        )}

        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography>
            Kto był uczestnikiem sytuacji, do której doszło? Identyfikacja
            uczestników sytuacji pomoże podjąć odpowiednie działania w tej
            sprawie. Jeśli jednak nie chcesz podawać jakichś danych, nie musisz
            tego robić.
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {affectType === 0
              ? "Ty (osoba potrzebująca pomocy w tej sytuacji)"
              : "Osoba potrzebująca pomocy"}
          </Typography>
        </Box>
        {((affectType === 0 && formType !== 0) ||
          (affectType === 1 && formType === 0) ||
          (affectType === 1 && formType !== 0)) && (
          <>
            <TextField
              fullWidth
              variant="filled"
              label={
                affectType === 0
                  ? "Twoje imię i nazwisko"
                  : "Imię i nazwisko osoby potrzebującej pomocy"
              }
              name="nameSurnameVictim"
              margin="normal"
              value={formik.values.nameSurnameVictim}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.nameSurnameVictim &&
                Boolean(formik.errors.nameSurnameVictim)
              }
              helperText={
                formik.touched.nameSurnameVictim &&
                formik.errors.nameSurnameVictim
                  ? formik.errors.nameSurnameVictim
                  : null
              }
              disabled={formType === 2 && affectType === 0}
            />
            {affectType === 0 && (
              <>
                <TextField
                  fullWidth
                  type="number"
                  variant="filled"
                  label="Twój numer telefonu"
                  name="phoneNumber"
                  margin="normal"
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.phoneNumber &&
                    Boolean(formik.errors.phoneNumber)
                  }
                  helperText={
                    formik.touched.phoneNumber && formik.errors.phoneNumber
                      ? formik.errors.phoneNumber
                      : "Twój telefon zostanie wykorzystany do nawiązania kontaktu z Tobą w celu weryfikacji tego, jak czujesz się po dokonaniu zgłoszenia i czy nie potrzebujesz dodatkowej pomocy oraz zostaną przekazane osobie prowadzącej interwencję w tej sprawie (jeśli nie jesteś nią Ty)."
                  }
                />

                <TextField
                  fullWidth
                  type="email"
                  variant="filled"
                  label="Twój adres e-mail"
                  name="emailAddress"
                  margin="normal"
                  value={formik.values.emailAddress}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.emailAddress &&
                    Boolean(formik.errors.emailAddress)
                  }
                  helperText={
                    formik.touched.emailAddress && formik.errors.emailAddress
                      ? formik.errors.emailAddress
                      : "Twój adres e-mail zostanie wykorzystany do kontaktu z Tobą."
                  }
                  disabled={formType === 2 && affectType === 0}
                />
              </>
            )}
          </>
        )}

        <TextField
          fullWidth
          select
          variant="filled"
          label={
            affectType === 0
              ? "Z jakiej chorągwi jesteś?"
              : "Z jakiej chorągwi jest osoba potrzebująca pomocy?"
          }
          name="regionalOffice"
          value={formik.values.regionalOffice}
          onChange={(e) => {
            formik.handleChange(e); // zaktualizuj Formik
            const selectedValue = e.target.value;

            formik.setFieldValue("districtOffice", ""); // resetujemy drugie pole

            if (selectedValue) {
              setLoadingDistrictOffices(true);
              axios
                .get(
                  `${process.env.REACT_APP_API_URL}/api/GetDistrictOffices?regionalOffice=${selectedValue}`
                )
                .then((res) => setDistrictOffices(res.data))
                .catch((err) => {
                  setError(
                    "[Hufce] Błąd pobierania danych hufców",
                    err && " (" + err.message + ")"
                  );
                })
                .finally(() => setLoadingDistrictOffices(false));
            }
          }}
          onBlur={formik.handleBlur}
          error={
            formik.touched.regionalOffice &&
            Boolean(formik.errors.regionalOffice)
          }
          helperText={
            formik.touched.regionalOffice && formik.errors.regionalOffice
              ? formik.errors.regionalOffice
              : "Pole wymagane. Po wybraniu Chorągwi załadują się właściwe Hufce."
          }
          margin="normal"
          disabled={loadingRegionalOffices}
        >
          {loadingRegionalOffices ? (
            <MenuItem disabled>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Ładowanie...
            </MenuItem>
          ) : (
            regionalOffices.map((opt) => (
              <MenuItem key={opt.objectKey} value={opt.objectKey}>
                {opt.label}
              </MenuItem>
            ))
          )}
        </TextField>

        {formik.values.regionalOffice && (
          <TextField
            fullWidth
            select
            variant="filled"
            label={
              affectType === 0
                ? "Z jakiego hufca jesteś?"
                : "Z jakiego hufca jest osoba potrzebująca pomocy?"
            }
            name="districtOffice"
            value={formik.values.districtOffice}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.districtOffice &&
              Boolean(formik.errors.districtOffice)
            }
            helperText={
              formik.touched.districtOffice && formik.errors.districtOffice
            }
            margin="normal"
            disabled={loadingDistrictOffices}
          >
            {loadingDistrictOffices ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Ładowanie...
              </MenuItem>
            ) : (
              districtOffices.map((item) => (
                <MenuItem key={item.objectKey} value={item.objectKey}>
                  {item.label}
                </MenuItem>
              ))
            )}
          </TextField>
        )}

        <TextField
          fullWidth
          type="number"
          variant="filled"
          label={
            affectType === 0
              ? "W jakim wieku jesteś?"
              : "W jakim wieku jest osoba potrzebująca pomocy?"
          }
          name="ageVictim"
          margin="normal"
          value={formik.values.ageVictim}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.ageVictim && Boolean(formik.errors.ageVictim)}
          helperText={formik.touched.ageVictim && formik.errors.ageVictim}
        />

        <TextField
          fullWidth
          variant="filled"
          label={
            affectType === 0
              ? "Twoja funkcja"
              : "Funkcja osoby potrzebującej pomocy"
          }
          name="roleVictim"
          margin="normal"
          value={formik.values.roleVictim}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.roleVictim && Boolean(formik.errors.roleVictim)}
          helperText={
            formik.touched.roleVictim && formik.errors.roleVictim
              ? formik.errors.roleVictim
              : affectType === 0
              ? "Jaką funkcję pełnisz w ZHP? Jeśli nie pełnisz żadnej, przejdź dalej."
              : "Jaką funkcję lub role pełni ta osoba? Czy jest szeregowym harcerzem, czy np. zastępowym lub drużynowym?"
          }
        />

        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            Osoba, która według Ciebie przekroczyła granice
          </Typography>
        </Box>

        <TextField
          fullWidth
          variant="filled"
          label="Imię i nazwisko tej osoby"
          name="nameSurnameGuilty"
          margin="normal"
          value={formik.values.nameSurnameGuilty}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.nameSurnameGuilty &&
            Boolean(formik.errors.nameSurnameGuilty)
          }
          helperText={
            formik.touched.nameSurnameGuilty && formik.errors.nameSurnameGuilty
              ? formik.errors.nameSurnameGuilty
              : "Jak nazywa się osoba, która według Ciebie przekroczyła granice? Możesz w to pole wpisać również grupę osób lub zostawić je puste."
          }
        />

        <TextField
          fullWidth
          type="number"
          variant="filled"
          label="W jakim wieku jest ta osoba?"
          name="ageGuilty"
          margin="normal"
          value={formik.values.ageGuilty}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.ageGuilty && Boolean(formik.errors.ageGuilty)}
          helperText={formik.touched.ageGuilty && formik.errors.ageGuilty}
        />

        <TextField
          fullWidth
          variant="filled"
          label="Kim jest ta osoba?"
          name="infoGuilty"
          margin="normal"
          multiline
          rows={4}
          value={formik.values.infoGuilty}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.infoGuilty && Boolean(formik.errors.infoGuilty)}
          helperText={
            formik.touched.infoGuilty && formik.errors.infoGuilty
              ? formik.errors.infoGuilty
              : "Podaj: funkcję, przynależność do środowiska (np. hufiec, chorągiew). Jeśli nie znasz jakichś danych tej osoby, napisz tak dużo jak wiesz."
          }
        />

        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            Opis sytuacji
          </Typography>
          <Typography>
            W tej sekcji poprosimy Cię o opisanie przebiegu sytuacji, do której
            doszło. Podaj te informacje, które znasz. Jeśli nie potrafisz
            odpowiedzieć na jakieś pytanie, omiń je.
          </Typography>
        </Box>

        <TextField
          fullWidth
          variant="filled"
          label="Opis sytuacji przekroczenia granic"
          name="description"
          multiline
          rows={4}
          margin="normal"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.description && Boolean(formik.errors.description)
          }
          helperText={
            formik.touched.description && formik.errors.description
              ? formik.errors.description
              : "Pole wymagane."
          }
        />
        <TextField
          fullWidth
          variant="filled"
          label="Czy jakieś działania zostały już podjęte?"
          name="actions"
          multiline
          rows={4}
          margin="normal"
          value={formik.values.actions}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.actions && Boolean(formik.errors.actions)}
          helperText={formik.touched.actions && formik.errors.actions}
          placeholder="Czy komenda hufca lub kierownik wypoczynku zareagował - w jaki sposób? Czy zostały przeprowadzone jakieś rozmowy w tej sprawie? Czy zostały wyciągnięte konsekwencje?"
        />
        <Box sx={{ mt: 2 }}>
          <FormControl>
            <FormLabel id="isCrime">
              Czy sytuacja nosi znamiona przestępstwa?
            </FormLabel>
            <RadioGroup
              aria-labelledby="isCrime"
              name="isCrime"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.isCrime}
              error={formik.errors.isCrime}
            >
              <FormControlLabel
                control={<Radio />}
                label="Tak"
                name="isCrime"
                value="Tak"
                sx={{ alignItems: "center" }}
              />
              <FormControlLabel
                value="Nie"
                control={<Radio />}
                label="Nie"
                name="isCrime"
              />
              <FormControlLabel
                value="Nie wiem"
                control={<Radio />}
                label="Nie wiem"
                name="isCrime"
              />
            </RadioGroup>
          </FormControl>
        </Box>
        {formik.values.isCrime === "Tak" && (
          <Box sx={{ mt: 2 }}>
            <FormControl>
              <FormLabel id="policeInformed">
                Czy o sytuacji poinformowano policję?
              </FormLabel>
              <RadioGroup
                aria-labelledby="policeInformed"
                name="policeInformed"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.policeInformed}
                error={formik.errors.policeInformed}
              >
                <FormControlLabel
                  control={<Radio />}
                  label="Tak"
                  name="policeInformed"
                  value="Tak"
                />
                <FormControlLabel
                  value="Nie"
                  control={<Radio />}
                  label="Nie"
                  name="policeInformed"
                />
                <FormControlLabel
                  value="Nie wiem"
                  control={<Radio />}
                  label="Nie wiem"
                  name="policeInformed"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <FormControl>
            <FormLabel id="isCamp">
              Czy do tej sytuacji doszło na obozie?
            </FormLabel>
            <RadioGroup
              aria-labelledby="isCamp"
              name="isCamp"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.isCamp}
              error={formik.errors.isCamp}
            >
              <FormControlLabel
                control={<Radio />}
                label="Tak"
                name="isCamp"
                value="Tak"
              />
              <FormControlLabel
                value="Nie"
                control={<Radio />}
                label="Nie"
                name="isCamp"
              />
            </RadioGroup>
          </FormControl>
        </Box>
        {formik.values.isCamp === "Tak" && (
          <>
            <TextField
              fullWidth
              variant="filled"
              label="Imię i nazwisko komendanta obozu (kierownika wypoczynku)"
              name="nameSurnameCampCommisioner"
              margin="normal"
              value={formik.values.nameSurnameCampCommisioner}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.nameSurnameCampCommisioner &&
                Boolean(formik.errors.nameSurnameCampCommisioner)
              }
              helperText={
                formik.touched.nameSurnameCampCommisioner &&
                formik.errors.nameSurnameCampCommisioner
                  ? formik.errors.nameSurnameCampCommisioner
                  : null
              }
            />
            <TextField
              fullWidth
              variant="filled"
              label="Adres e-mail komendanta obozu (kierownika wypoczynku)"
              name="emailAddressCampCommisioner"
              margin="normal"
              value={formik.values.emailAddressCampCommisioner}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.emailAddressCampCommisioner &&
                Boolean(formik.errors.emailAddressCampCommisioner)
              }
              helperText={
                formik.touched.emailAddressCampCommisioner &&
                formik.errors.emailAddressCampCommisioner
                  ? formik.errors.emailAddressCampCommisioner
                  : null
              }
            />
            <TextField
              fullWidth
              type="number"
              variant="filled"
              label="Numer telefonu komendanta obozu (kierownika wypoczynku)"
              name="phoneNumberCampCommisioner"
              margin="normal"
              value={formik.values.phoneNumberCampCommisioner}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.phoneNumberCampCommisioner &&
                Boolean(formik.errors.phoneNumberCampCommisioner)
              }
              helperText={
                formik.touched.phoneNumberCampCommisioner &&
                formik.errors.phoneNumberCampCommisioner
                  ? formik.errors.phoneNumberCampCommisioner
                  : null
              }
            />
          </>
        )}
        <Box sx={{ mt: 2 }}>
          <FormControl>
            <FormLabel id="isDistrictCommisionerGuilty">
              Czy chcesz, aby Komendant Hufca{" "}
              <strong>NIE został poinformowany</strong> o tym zgłoszeniu i aby
              było ono rozpatrywana na poziomie Chorągwi?
            </FormLabel>
            <RadioGroup
              aria-labelledby="isDistrictCommisionerGuilty"
              name="isDistrictCommisionerGuilty"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.isDistrictCommisionerGuilty}
              error={formik.errors.isDistrictCommisionerGuilty}
            >
              <FormControlLabel
                control={<Radio />}
                label="Tak"
                name="isDistrictCommisionerGuilty"
                value="Tak"
              />
              <FormControlLabel
                value="Nie"
                control={<Radio />}
                label="Nie"
                name="isDistrictCommisionerGuilty"
              />
            </RadioGroup>
          </FormControl>
        </Box>
        <Box sx={{ mt: 2 }}>
          <FormControl>
            <FormLabel id="isRegionalOfficeCommisionerGuilty">
              Czy chcesz, aby Komendant Chorągwi{" "}
              <strong>NIE został poinformowany</strong> o tym zgłoszeniu i aby
              było ono rozpatrywana na poziomie Głównej Kwatery?
            </FormLabel>
            <RadioGroup
              aria-labelledby="isRegionalOfficeCommisionerGuilty"
              name="isRegionalOfficeCommisionerGuilty"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.isRegionalOfficeCommisionerGuilty}
              error={formik.errors.isRegionalOfficeCommisionerGuilty}
            >
              <FormControlLabel
                control={<Radio />}
                label="Tak"
                name="isRegionalOfficeCommisionerGuilty"
                value="Tak"
              />
              <FormControlLabel
                value="Nie"
                control={<Radio />}
                label="Nie"
                name="isRegionalOfficeCommisionerGuilty"
              />
            </RadioGroup>
          </FormControl>
        </Box>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={formik.isSubmitting}
          sx={{
            backgroundColor: "#850C70",
            "&:hover": {
              backgroundColor: "#5F0054",
            },
            mt: 2,
          }}
        >
          Wyślij
        </Button>
        <Button
          onClick={formik.handleReset}
          variant="outlined"
          sx={{
            ml: 2,
            mt: 2,
            borderColor: "#850C70",
            color: "#850C70",
            "&:hover": {
              backgroundColor: "#5F0054",
              color: "#fff",
              borderColor: "#5F0054",
            },
          }}
        >
          Anuluj
        </Button>
      </Box>
    </>
  );
}
